import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { PackageCheck, LogOut, Loader2, RefreshCw, MailOpen, Inbox } from "lucide-react";
import { toast } from "sonner";
import { API, money } from "../lib/api";
import { Logo } from "../components/site/Logo";

const CARRIERS = ["USPS", "UPS", "FedEx", "DHL", "Other"];

const ShipForm = ({ order, onDone }) => {
  const [carrier, setCarrier] = useState("USPS");
  const [tracking, setTracking] = useState("");
  const [busy, setBusy] = useState(false);
  const ship = async () => {
    if (tracking.trim().length < 4) return toast.error("Enter a tracking number (at least 4 characters)");
    setBusy(true);
    try {
      await axios.post(`${API}/admin/orders/${order.session_id}/ship?token=${localStorage.getItem("ldm_admin")}`,
        { carrier, tracking_number: tracking.trim() });
      toast.success(`Shipping email sent to ${order.confirmation_email || "the customer"}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the shipping email");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid={`ship-form-${order.session_id.slice(-8)}`} className="mt-4 flex flex-wrap items-center gap-2">
      <select value={carrier} onChange={(e) => setCarrier(e.target.value)} data-testid="ship-carrier-select" className="rounded-full border border-plum/20 bg-white px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-magenta">
        {CARRIERS.map((c) => <option key={c}>{c}</option>)}
      </select>
      <input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Tracking number" data-testid="ship-tracking-input" className="min-w-0 flex-1 rounded-full border border-plum/20 bg-white px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-magenta" />
      <button onClick={ship} disabled={busy} data-testid="ship-notify-button" className="btn-luxe !px-5 !py-2.5 disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackageCheck className="h-4 w-4" />} Mark shipped &amp; email
      </button>
    </div>
  );
};

const StockRow = ({ p, onSave }) => {
  const [val, setVal] = useState(p.stock ?? 0);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/inventory/${p.id}?token=${localStorage.getItem("ldm_admin")}`, { stock: Number(val) });
      toast.success(`${p.name}: stock set to ${val}`);
      onSave();
    } catch {
      toast.error("Could not save stock");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid={`stock-row-${p.id}`} className="flex items-center gap-3 rounded-2xl bg-white p-3 shadow-[0_10px_30px_-20px_rgba(42,15,46,.3)]">
      <img src={p.image} alt={p.name} className="h-12 w-10 rounded-lg object-cover" />
      <div className="min-w-0 flex-1">
        <p className="truncate font-display text-base leading-tight">{p.name}</p>
        <p className="text-[10px] uppercase tracking-[0.15em] text-plum/60">{p.category}{p.stock === 0 ? " · Sold out" : p.stock <= 5 ? " · Low" : ""}</p>
      </div>
      <input
        type="number"
        min="0"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        data-testid={`stock-input-${p.id}`}
        className="w-20 rounded-full border border-plum/20 px-4 py-2 text-center text-sm focus:outline-none focus:ring-2 focus:ring-magenta"
      />
      <button onClick={save} disabled={busy} data-testid={`stock-save-${p.id}`} className="btn-luxe !px-4 !py-2 !text-[10px]">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Save
      </button>
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("ldm_admin") || "");
  const [password, setPassword] = useState("");
  const [orders, setOrders] = useState(null);
  const [messages, setMessages] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async (t = token) => {
    try {
      const [o, m, inv] = await Promise.all([
        axios.get(`${API}/admin/orders?token=${t}`),
        axios.get(`${API}/admin/messages?token=${t}`),
        axios.get(`${API}/admin/inventory?token=${t}`),
      ]);
      setOrders(o.data);
      setMessages(m.data);
      setInventory(inv.data);
    } catch {
      if (t) { localStorage.removeItem("ldm_admin"); setToken(""); }
      setOrders([]);
      setMessages([]);
      setInventory([]);
    }
  };
  useEffect(() => { if (token) load(token); }, []); // eslint-disable-line

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem("ldm_admin", res.data.token);
      setToken(res.data.token);
      load(res.data.token);
    } catch {
      toast.error("Wrong password");
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <main data-testid="admin-login-page" className="grid min-h-screen place-items-center bg-cream px-5">
        <form onSubmit={login} className="w-full max-w-sm rounded-[2rem] bg-white p-8 shadow-[0_20px_60px_-30px_rgba(42,15,46,.35)] text-center">
          <Logo testId="admin-logo" />
          <p className="eyebrow mt-8 text-gold-deep">Owner area</p>
          <h1 className="mt-2 font-display text-3xl italic">Order Desk</h1>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Admin password" data-testid="admin-password-input" className="mt-6 w-full rounded-full border border-plum/20 px-5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-magenta" />
          <button type="submit" disabled={busy} data-testid="admin-login-button" className="btn-luxe mt-4 w-full">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Sign in</button>
          <Link to="/" data-testid="admin-back-link" className="mt-4 block text-xs font-semibold uppercase tracking-[0.2em] text-magenta">Back to store</Link>
        </form>
      </main>
    );
  }

  return (
    <main data-testid="admin-page" className="min-h-screen bg-cream px-4 sm:px-8 py-10">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Logo testId="admin-logo" />
            <span className="hidden sm:block h-8 w-px bg-plum/15" />
            <div className="hidden sm:block">
              <p className="eyebrow text-gold-deep">Owner area</p>
              <p className="font-display text-2xl italic">Order Desk</p>
            </div>
          </div>
          <div className="flex gap-2">
            {messages?.some((m) => !m.read) && (
              <span data-testid="admin-unread-badge" className="inline-flex items-center gap-1.5 rounded-full bg-magenta px-4 py-2 text-[11px] font-bold uppercase tracking-[0.15em] text-white">
                <Inbox className="h-3.5 w-3.5" /> {messages.filter((m) => !m.read).length} new
              </span>
            )}
            <button onClick={() => load()} data-testid="admin-refresh-button" className="btn-ghost !px-5 !py-2.5"><RefreshCw className="h-4 w-4" /> Refresh</button>
            <button onClick={() => { localStorage.removeItem("ldm_admin"); setToken(""); }} data-testid="admin-logout-button" className="btn-ghost !px-5 !py-2.5"><LogOut className="h-4 w-4" /> Log out</button>
          </div>
        </div>

        {orders === null ? (
          <p className="py-24 text-center font-display text-2xl italic text-plum/60">Loading orders…</p>
        ) : orders.length === 0 ? (
          <div data-testid="admin-empty-state" className="py-24 text-center">
            <p className="font-script text-5xl text-plum">No paid orders yet</p>
            <p className="mt-3 text-plum/70">Paid orders appear here after shoppers check out.</p>
          </div>
        ) : (
          <div className="mt-10 space-y-5">
            {orders.map((o) => (
              <div key={o.session_id} data-testid={`admin-order-${o.session_id.slice(-8)}`} className="rounded-[1.75rem] bg-white p-6 shadow-[0_16px_50px_-30px_rgba(42,15,46,.35)]">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="eyebrow text-[10px] text-plum/60">Order {o.session_id.slice(-14)}</p>
                    <p className="mt-1 font-display text-xl">
                      {(o.summary ?? []).map((i) => `${i.name} ×${i.quantity}${i.size ? ` (${i.size})` : ""}`).join(" · ")}
                    </p>
                    <p className="mt-1 text-sm text-plum/75">
                      {o.confirmation_email || "customer email on file"} · {new Date(o.updated_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-display text-2xl">{money(o.amount)}</p>
                    <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.15em]">
                      {o.shipped ? <span className="text-teal">Shipped · notified</span> : o.confirmation_sent ? <span className="text-magenta">Confirmation sent</span> : <span className="text-plum/60">Paid</span>}
                    </p>
                  </div>
                </div>
                {!o.shipped && <ShipForm order={o} onDone={() => load()} />}
                {o.shipped && (
                  <p data-testid={`shipped-info-${o.session_id.slice(-8)}`} className="mt-4 text-sm text-plum/75">
                    Shipped via {o.shipped.carrier} · tracking {o.shipped.tracking_number}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        <section className="mt-16">
          <h2 data-testid="admin-stock-heading" className="font-script text-5xl text-plum">Stock Room</h2>
          <p className="mt-2 text-sm text-plum/70">Set how many of each piece you have. When stock hits zero the item shows "Sold out" on the site automatically; 5 or fewer shows "Only X left".</p>
          {inventory === null ? (
            <p className="py-10 text-center font-display text-xl italic text-plum/60">Loading stock…</p>
          ) : (
            <div className="mt-8 grid gap-2.5 sm:grid-cols-2" data-testid="admin-stock-list">
              {inventory.map((p) => <StockRow key={p.id} p={p} onSave={() => load()} />)}
            </div>
          )}
        </section>

        <section className="mt-16">
          <h2 data-testid="admin-messages-heading" className="font-script text-5xl text-plum">Contact Inbox</h2>
          <p className="mt-2 text-sm text-plum/70">Messages from the Contact form on your site.</p>
          {messages === null ? (
            <p className="py-10 text-center font-display text-xl italic text-plum/60">Loading messages…</p>
          ) : messages.length === 0 ? (
            <div data-testid="admin-messages-empty" className="py-14 text-center">
              <p className="font-display text-2xl italic text-plum/75">No messages yet.</p>
            </div>
          ) : (
            <div className="mt-8 space-y-4">
              {messages.map((m) => (
                <div key={m._id} data-testid={`admin-message-${m._id}`} className={`rounded-[1.5rem] p-6 shadow-[0_16px_50px_-30px_rgba(42,15,46,.35)] ${m.read ? "bg-white/70" : "bg-white border-l-4 border-magenta"}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-display text-lg">{m.name}</p>
                      <p className="text-sm text-plum/70">{m.email}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="text-xs text-plum/60">{new Date(m.created_at).toLocaleString()}</p>
                      {!m.read && (
                        <button
                          data-testid={`admin-message-read-${m._id}`}
                          onClick={async () => {
                            try {
                              await axios.post(`${API}/admin/messages/${m._id}/read?token=${localStorage.getItem("ldm_admin")}`);
                              load();
                            } catch { toast.error("Could not mark as read"); }
                          }}
                          className="inline-flex items-center gap-1.5 rounded-full border border-plum/20 px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-plum hover:text-cream"
                        >
                          <MailOpen className="h-3.5 w-3.5" /> Mark read
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="mt-3 leading-relaxed text-plum/85">{m.message}</p>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
